import { makeAutoObservable } from 'mobx';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import {
    BonusCampaignFormType,
    CampaignStatusType,
    CampaignTypeZod,
    FrequencyPeriodZod,
    StartAtTypeType,
    StartAtTypeZod,
    TimeLimitTriggerZod,
} from 'src/api_openapi/v2/bonus-engine/schemas';
import { validateNotEmpty } from 'src_common/common/mobx-utils/Form2/validators';
import {
    validateNotEmptyIf,
    isDateAfter,
    isNumericBetween,
    validateIf,
} from 'src/domains/casino/webview/bonus-engine/campaign/CampaignCreator.validators';
import { FormModel, Result } from 'src_common/common/mobx-utils/Form2/FormModel';
import { CampaignCreatorState, zodGuard } from 'src/domains/casino/webview/bonus-engine/campaign/CampaignCreator.state';
import {
    apiDateToFormDate,
    formDateToApiDate,
} from 'src/domains/casino/webview/bonus-engine/campaign/CampaignCreator.helpers';
import { CampaignModel } from 'src/domains/casino/state/AppCasino/store/campaign-store/CampaignModel';
import { Common } from 'src/AppState/Common';
import { CampaignStore } from 'src/domains/casino/state/AppCasino/store/campaign-store/Campaign.store';
import { validateLength } from 'src/api/utils/form2Validators';

export type GeneralDetailsFormType = {
    campaignTypeForm: string;
    nameForm: string;
    descriptionForm: string;
    optInOnlyForm: boolean;
    startType: string;
    startAtForm: string;
    expiryAtForm: string;
    frequencyPeriodForm: string;
    numberOfUsesForm: string;
    timeLimitForm: string;
    timeLimitTriggerForm: string;
};

export class GeneralDetailsState {
    public readonly nameForm: FormInputState<string, string>;
    public readonly termsAndConditionsForm: FormInputState<string, string>;
    public readonly optInOnlyForm: FormInputState<boolean, boolean>;
    public readonly campaignTypeForm: FormInputState<string, string>;
    public startAtForm: FormInputState<string, string>;
    public readonly expiryAtForm: FormInputState<string, string>;
    public readonly frequencyPeriodForm: FormInputState<string, string>;
    public readonly numberOfUsesForm: FormInputState<string, string>;
    public readonly startTypeForm: FormInputState<StartAtTypeType, string>;
    public readonly timeLimitForm: FormInputState<string, string>;
    public readonly timeLimitTriggerForm: FormInputState<string, string>;
    private readonly form: FormModel<GeneralDetailsFormType>;
    public campaignStatus: CampaignStatusType | null = null;
    private readonly campaignStore: CampaignStore;

    private readonly noEmptyValidationMessage = 'Required field';

    public constructor(
        private common: Common,
        private campaignId: string | undefined
    ) {
        makeAutoObservable(this);
        this.campaignStore = CampaignStore.get(this.common);

        this.campaignTypeForm = FormInputState.new('').map(validateNotEmpty(this.noEmptyValidationMessage));
        this.nameForm = FormInputState.new('')
            .map(validateLength(100))
            .map(validateNotEmpty(this.noEmptyValidationMessage));

        this.termsAndConditionsForm = FormInputState.new('').map(validateNotEmpty(this.noEmptyValidationMessage));
        this.optInOnlyForm = FormInputState.new(false).map((value) => {
            const promoCodesState = CampaignCreatorState.get(this.common, this.campaignId).promoCodesState;

            if (promoCodesState.isEmpty() === false && value === true) {
                return Result.createError(`'Require User Opt-In' can not be selected when Promo Code is enabled`);
            }
            return Result.createOk(value);
        });
        this.startTypeForm = FormInputState.new<StartAtTypeType>('once-campaign-activated').map(
            validateNotEmpty(this.noEmptyValidationMessage)
        );
        this.startAtForm = FormInputState.new('')
            .map(validateNotEmptyIf(this.noEmptyValidationMessage, () => this.startTypeForm.value === 'custom'))
            .map(isDateAfter(() => new Date().toString(), false, "Start date can't be in the past"));
        this.expiryAtForm = FormInputState.new('')
            .map(validateNotEmpty(this.noEmptyValidationMessage))
            .map(isDateAfter(() => this.startAtForm.value, false, 'End date should be after start date'))
            .map(isDateAfter(() => new Date().toString(), false, "End date can't be in the past"));
        this.frequencyPeriodForm = FormInputState.new('').map((value) => {
            return validateIf(
                () => this.timeLimitTriggerForm.value === '',
                () => validateNotEmpty(this.noEmptyValidationMessage)(value)
            );
        });
        this.numberOfUsesForm = FormInputState.new('')
            .map((value) => {
                return validateIf(
                    () => this.frequencyPeriodForm.value !== '',
                    () => validateNotEmpty('Enter a number between 1 and 100.')(value)
                );
            })
            .map((value) => {
                return validateIf(
                    () => this.frequencyPeriodForm.value !== '',
                    () => isNumericBetween({ from: 1, to: 100 })(value)
                );
            });
        this.timeLimitForm = FormInputState.new('')
            .map((value) => {
                return validateIf(
                    () => this.timeLimitTriggerForm.value !== '',
                    () => validateNotEmpty(this.noEmptyValidationMessage)(value)
                );
            })
            .map((value) => {
                return validateIf(
                    () => this.timeLimitTriggerForm.value !== '',
                    () => isNumericBetween({ from: 1, to: 100 })(value)
                );
            });

        this.timeLimitTriggerForm = FormInputState.new('').map(this.validateTimeLimitTrigger);

        this.form = FormModel.group({
            campaignTypeForm: this.campaignTypeForm,
            nameForm: this.nameForm,
            descriptionForm: this.termsAndConditionsForm,
            optInOnlyForm: this.optInOnlyForm,
            startType: this.startTypeForm,
            startAtForm: this.startAtForm,
            expiryAtForm: this.expiryAtForm,
            frequencyPeriodForm: this.frequencyPeriodForm,
            numberOfUsesForm: this.numberOfUsesForm,
            timeLimitForm: this.timeLimitForm,
            timeLimitTriggerForm: this.timeLimitTriggerForm,
        });
    }

    public get campaignDisplayName(): string {
        return this.nameForm.value.length > 0 ? this.nameForm.value : 'Untitled';
    }

    public get name(): BonusCampaignFormType['name'] {
        if (this.nameForm.value === '') {
            return undefined;
        }

        return this.nameForm.value;
    }

    public get termsAndConditions(): BonusCampaignFormType['description'] {
        if (this.termsAndConditionsForm.value === '') {
            return undefined;
        }
        return this.termsAndConditionsForm.value;
    }

    public get optInOnly(): BonusCampaignFormType['optInOnly'] {
        return this.optInOnlyForm.value;
    }

    public get campaignType(): BonusCampaignFormType['campaignType'] {
        if (this.campaignTypeForm.value.trim() === '') {
            return undefined;
        }
        return zodGuard(this.campaignTypeForm.value, CampaignTypeZod);
    }
    public get timeLimitTrigger(): BonusCampaignFormType['timeLimitTrigger'] {
        if (this.timeLimitTriggerForm.value.trim() === '') {
            return undefined;
        }
        return zodGuard(this.timeLimitTriggerForm.value, TimeLimitTriggerZod);
    }

    public get timeLimit(): BonusCampaignFormType['timeLimit'] {
        const parsedNumber = parseInt(this.timeLimitForm.value, 10);
        if (isNaN(parsedNumber)) {
            return undefined;
        }
        return parsedNumber;
    }

    public get startAt(): BonusCampaignFormType['startAt'] {
        return formDateToApiDate(this.startAtForm.value);
    }

    public get expiryAt(): BonusCampaignFormType['expiryAt'] {
        return formDateToApiDate(this.expiryAtForm.value);
    }

    public get frequencyPeriod(): BonusCampaignFormType['frequencyPeriod'] {
        if (this.frequencyPeriodForm.value.trim() === '') {
            return undefined;
        }
        return zodGuard(this.frequencyPeriodForm.value, FrequencyPeriodZod);
    }

    public get numberOfUses(): BonusCampaignFormType['numberOfUses'] {
        const parsedNumber = parseInt(this.numberOfUsesForm.value, 10);
        if (isNaN(parsedNumber)) {
            return undefined;
        }
        return parsedNumber;
    }

    public get startType(): StartAtTypeType {
        const startType = this.startTypeForm.value;

        const asType = StartAtTypeZod.safeParse(startType);
        if (asType.success === false) {
            throw new Error('Invalid start type');
        }
        return asType.data;
    }

    private get isPromoCampaign(): boolean {
        return this.campaignTypeForm.value === 'promo-code';
    }

    private get isValidFrequencyPeriod(): boolean {
        return this.frequencyPeriodForm.value === 'one-time';
    }

    private get isOptInOnly(): boolean {
        return this.optInOnlyForm.value === true;
    }

    private get isPlayerTypeNewPlayers(): boolean {
        const { playerManagementState } = CampaignCreatorState.get(this.common, this.campaignId);
        return playerManagementState.playerType.value === 'new-players';
    }

    private validateTimeLimitTrigger = (value: string): Result<string> => {
        if (this.isPromoCampaign && value !== '') {
            return Result.createError('Time limit can not be selected for Promo Code campaign type');
        }

        if (value === 'opt-in') {
            if (!this.isOptInOnly) {
                return Result.createError("Opt In trigger can be selected when 'Require User Opt-In' is enabled");
            }
        }

        if (!this.isValidFrequencyPeriod && value !== '') {
            return Result.createError('Time limit can be selected when usage frequency is Only Once');
        }

        if (value === 'account-creation') {
            if (this.isOptInOnly) {
                return Result.createError(
                    "Account creation trigger can not be selected when 'Require User Opt-In' is enabled"
                );
            }

            if (!this.isPlayerTypeNewPlayers) {
                return Result.createError('Account creation trigger can be selected when player type is New Players');
            }
        }

        return Result.createOk(value);
    };

    public validNotEmpty(): void {
        this.form.mapChild((item) => {
            if (item instanceof FormInputState) {
                const value = item.value;
                if (value.trim() !== '') {
                    item.setAsVisited();
                }
            }

            return item;
        });
    }

    public isTouched(): boolean {
        return this.form.isTouched();
    }

    public setIsTouched(): void {
        this.form.setIsTouched();
    }

    public resetIsTouched(): void {
        this.form.resetIsTouched();
    }

    public resetIsVisited(): void {
        this.form.resetIsVisited();
    }

    public validateForm(): boolean {
        this.form.setAsVisited();
        return this.form.result.value.type === 'ok';
    }

    public get formErrorsCount(): number {
        return this.form.errorsForView.length;
    }

    public get startDateDisabled(): boolean {
        if (this.startTypeForm.value === 'once-campaign-activated') {
            return true;
        }
        return false;
    }

    public get endDateDisabled(): boolean {
        return this.campaignStatus === 'expired';
    }

    public setFormValues = async (campaignModel: CampaignModel): Promise<void> => {
        this.campaignStatus = campaignModel.status;

        const startAtDate = apiDateToFormDate(campaignModel.startTimeApi);
        const expiryAtDate = apiDateToFormDate(campaignModel.endTimeApi);

        this.nameForm.setValue(campaignModel.campaignName);
        this.termsAndConditionsForm.setValue(campaignModel.description);
        this.optInOnlyForm.setValue(campaignModel.optInOnly ?? false);
        this.campaignTypeForm.setValue(campaignModel.campaignType);
        this.startTypeForm.setValue(campaignModel.startTime === null ? 'once-campaign-activated' : 'custom');
        this.startAtForm.setValue(startAtDate);
        this.expiryAtForm.setValue(expiryAtDate);
        this.frequencyPeriodForm.setValue(campaignModel.frequencyPeriod ?? '');
        this.numberOfUsesForm.setValue(campaignModel.numberOfUses?.toString() ?? '');
        this.timeLimitForm.setValue(campaignModel.timeLimit?.toString() ?? '');
        this.timeLimitTriggerForm.setValue(campaignModel.timeLimitTrigger ?? '');
    };

    public get campaign(): CampaignModel | null {
        if (this.campaignId === undefined) {
            return null;
        }
        return this.campaignStore.getCampaign(this.campaignId);
    }
}
